import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Home from '../Home';

// --- 1. Global Styles and Layout Setup ---

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    color: #333;
    line-height: 1.6;
    background-color: #fff;
  }

  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const Section = styled.section`
  padding: 40px 20px;
  text-align: center;
`;

const PrimaryButton = styled.a`
  display: inline-block;
  background-color: #8cc63f; /* Closest to the green in the image */
  color: white;
  padding: 12px 30px;
  text-decoration: none;
  font-weight: bold;
  border-radius: 5px;
  transition: background-color 0.3s;
  text-transform: uppercase;
  font-size: 0.9em;

  &:hover {
    background-color: #7ab32e;
  }
`;

// --- 2. Header Section ---

const HeaderSection = styled.header`
  background-color: #222;
  background-image: url('YOUR_MAGNIFYING_GLASS_BACKGROUND_IMAGE_URL');
  background-size: cover;
  background-position: center;
  color: white;
  padding: 100px 20px 150px; /* Adjusted padding for better visual height */
  position: relative;
  text-align: center;
`;

const HeaderTitle = styled.h1`
  font-size: 3em;
  font-weight: 700;
  margin-bottom: 30px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
`;

const ContactButton = styled(PrimaryButton)`
  background-color: transparent;
  border: 2px solid white;
  font-size: 1em;
`;

// --- 3. Why Choose Section (The Numbered List) ---

const WhyChooseSection = styled(Section)`
  padding-top: 80px;
  padding-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-size: 2em;
  font-weight: 700;
  color: #333;
  margin-bottom: 50px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  text-align: left;
  margin-bottom: 30px;
  width: 100%;
`;

const FeatureNumber = styled.div`
  font-size: 2em;
  font-weight: bold;
  color: #333;
  margin-right: 20px;
  padding-right: 15px;
  border-right: 2px solid #ccc; /* Mimics the vertical line */
  line-height: 1;
`;

const FeatureContent = styled.div`
  flex: 1;

  h3 {
    font-size: 1.2em;
    font-weight: bold;
    color: #333;
    margin-bottom: 5px;
  }

  p {
    font-size: 0.9em;
    color: #666;
    margin: 0;
  }
`;

// --- 4. How Will WooCommerce Help Section ---

const HelpSection = styled(Section)`
  padding: 60px 20px;
  background-color: #f7f7f7;
`;

const FlowChart = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px 0;
  /* Placeholder for the chart image/graphics */
  height: 80px;
  background-color: #eee;
  border: 1px solid #ddd;
  border-radius: 5px;
  line-height: 80px;
  font-style: italic;
  color: #999;
`;

const ExcellenceBox = styled.div`
  max-width: 800px;
  margin: 40px auto;
  border: 1px solid #ddd;
  padding: 30px;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);

  h3 {
    color: #333;
    font-size: 1.5em;
    font-weight: bold;
    margin-bottom: 15px;
  }

  p {
    font-size: 0.9em;
    color: #666;
  }
`;

// --- 5. Why Is WooCommerce Important Section (The Icon Grid) ---

const ImportanceSection = styled(Section)`
  padding: 80px 20px;
`;

const FeatureGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
`;

const GridItem = styled.div`
  width: 300px; /* Fixed width for better control */
  text-align: center;
  padding: 15px;

  h4 {
    font-size: 1em;
    font-weight: bold;
    margin-top: 10px;
    margin-bottom: 5px;
  }

  p {
    font-size: 0.8em;
    color: #666;
    margin: 0;
  }
`;

const IconAndText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;

  span {
    font-size: 1.1em;
    font-weight: bold;
    margin-left: 10px;
  }
`;

const IconPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  background-color: #f0f0f0; /* Placeholder background */
  border-radius: 50%;
  border: 1px solid #ccc;
  /* Add specific background images here for real icons */
`;

// --- 6. Main App Component ---

const features = [
  { num: 1, title: "SRK Softwares Experts", description: "SRK Softwares is a top-tier firm specializing in WooCommerce Design & Development. We spend a lot of time learning and perfecting our techniques, so our clients can benefit from superior service. We're the folks who know just how to set up a market that looks great, and makes sure everything runs smoothly." },
  { num: 2, title: "Make It Yours", description: "Imagine your online store as a tiny shop or a huge store. SRK Softwares Tech can give it the perfect paint job, no matter the size. We make custom designs that truly represent your brand. Your products will sparkle, and your store looks like a million bucks—or what people want to see when they're looking at home." },
  { num: 3, title: "Works Everywhere", description: "Your store needs to work on all devices—phones, tablets, and desktop. SRK Softwares Tech makes sure it does. We make sure what happens on your store—from the stuff your shoppers check out to the special sale announcements—works perfectly on all devices. You can be sure that your customers will always be able to shop, from anywhere." },
  { num: 4, title: "Help When You Need It:", description: "Even the best stores might run into a snag or need a little adjustment. For any issues that crop up, we're right here to help you. Our support team is super fast and ready. We're like a friend who's good with techie things. They're just an email or a call away, ready to help you out and keep your store running perfectly." },
  { num: 5, title: "Get More Sales", description: "You know what's even better than a beautiful e-shop? The key thing is how to design your shop to make customers buy more. SRK Softwares Tech knows the tricks to do it. We'll set up special deals and smart layouts that gently push people to add more to their carts. Everything's designed to give your sales a little nudge, making it a breeze for SRK Softwares to see more customers." },
  { num: 6, title: "They Care About Your Success", description: "SRK Softwares Tech is really serious about helping your business. We don't just set up the shop; we're invested in your success. They'll give you your own team of cheerleaders, sending reports to make sure your store is growing, offering tips to sell even more, and making sure everything is always up-to-date. You can trust that they've got your back." },
];

const importanceFeatures = [
  { title: "Easy Store Setup", description: "We'll handle all the tricky stuff, leaving you to focus on your core business.", icon: 'Icon' },
  { title: "Gets You Seen", description: "Optimized for search engines, your shop and products reach more people.", icon: 'Icon' },
  { title: "Saves Money", description: "Avoid expensive monthly fees of other platforms, saving you operational cost.", icon: 'Icon' },
  { title: "Shows You Info", description: "Detailed analytics on what people are buying and what they like, so you can make your store better.", icon: 'Icon' },
  { title: "Customizes to You", description: "Your store fits your brand perfectly, with unique features and design touches.", icon: 'Icon' },
  { title: "Grows with You", description: "As your business gets bigger, WooCommerce can handle more products and customers easily.", icon: 'Icon' },
];

function Woocommerce() {
  return (
    <Home>
      <GlobalStyle />
      <HeaderSection>
        <HeaderTitle>
          Powering Your Online Store With Simplicity And Flexibility
        </HeaderTitle>
        <ContactButton href="#">CONTACT NOW</ContactButton>
      </HeaderSection>

      <PageContainer>
        <WhyChooseSection>
          <SectionTitle>
            Why should you choose SRK Softwares for your WooCommerce Design & Development?
          </SectionTitle>
          <FeatureList>
            {features.map((feature) => (
              <FeatureItem key={feature.num}>
                <FeatureNumber>{feature.num}</FeatureNumber>
                <FeatureContent>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </FeatureContent>
              </FeatureItem>
            ))}
          </FeatureList>
        </WhyChooseSection>

        <HelpSection>
          <SectionTitle>
            How Will WooCommerce Help My Business?
          </SectionTitle>

          <FlowChart>
            {/* Placeholder for the flow chart: Setup -> Design -> Products -> Strategy -> Launch */}
            FLOW CHART GRAPHICS HERE
          </FlowChart>

          <ExcellenceBox>
            <h3>E-COMMERCE EXCELLENCE</h3>
            <p>
              Digital marketing provides our clients with the ultimate, all-inclusive e-commerce solution. We can easily promote the products/services, design and get them set up on the best e-commerce platform. You need the right pilot strategy, and we are the only people who know how to expertly craft and successfully launch your store.
            </p>
          </ExcellenceBox>
          <PrimaryButton href="#">GET A FREE STRATEGY</PrimaryButton>
        </HelpSection>

        <ImportanceSection>
          <SectionTitle>
            Why is WooCommerce important for your business?
          </SectionTitle>
          <p>
            We are a team of passionate professionals, developers, and certified specialists, specializing in everything front-end discovery, innovation, and implementation. We ensure that our clients have the best online presence possible.
          </p>

          <FeatureGrid>
            {importanceFeatures.map((item) => (
              <GridItem key={item.title}>
                <IconAndText>
                  <IconPlaceholder />
                  <span>{item.title}</span>
                </IconAndText>
                <p>{item.description}</p>
              </GridItem>
            ))}
          </FeatureGrid>
        </ImportanceSection>
      </PageContainer>
    </Home>
  );
}

export default Woocommerce;

